import { useId, useState, type CSSProperties } from "react";
import type { ArticleFigure, BarFigure, FigureRow } from "../../lib/figures";

type Ref = NonNullable<BarFigure["refs"]>[number];

/**
 * A chart in an article: horizontal bars from zero, or dots on a log axis.
 * Every label and value is text in the server's HTML; the bars, dots and
 * lines are drawing only. Hovering a row lifts it and shows its value in a
 * tooltip, which repeats what the row already says, so the rows are not tab
 * stops.
 */
export function Figure({ figure }: { figure: ArticleFigure }) {
  const id = useId();
  const [active, setActive] = useState<number | null>(null);
  const position =
    figure.kind === "bars" ? (value: number) => value / figure.max : logPosition(figure.ticks);
  const refs = figure.kind === "bars" ? (figure.refs ?? []) : [];
  const ticks = figure.ticks.map((tick) => position(tick));

  return (
    <figure
      className="art-fig"
      data-kind={figure.kind}
      data-active={active === null ? undefined : ""}
      aria-labelledby={`${id}-title`}
    >
      <figcaption className="art-fig-head">
        <span className="art-fig-title" id={`${id}-title`}>
          {figure.title}
        </span>
        {figure.caption ? <span className="art-fig-caption">{figure.caption}</span> : null}
      </figcaption>

      {refs.length > 0 ? (
        <ul className="art-fig-legend" aria-label="Lines on the chart">
          {refs.map((ref) => (
            <li key={ref.label} data-tone={ref.tone}>
              <span className="art-fig-key" aria-hidden="true" />
              {ref.label}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="art-chart">
        <div className="art-chart-axis" aria-hidden="true">
          <span />
          <span className="art-chart-ticks">
            {figure.ticks.map((tick, i) => (
              <span
                key={tick}
                style={at(ticks[i])}
                data-edge={i === 0 ? "start" : i === figure.ticks.length - 1 ? "end" : undefined}
              >
                {tick.toLocaleString("en-US")}
              </span>
            ))}
          </span>
        </div>
        <ol className="art-chart-rows">
          {figure.rows.map((row, i) => (
            <Row
              key={`${row.label}-${row.detail ?? ""}`}
              row={row}
              kind={figure.kind}
              place={position(row.value)}
              ticks={ticks}
              refs={refs.map((ref) => ({ ...ref, place: position(ref.value) }))}
              active={active === i}
              onActive={(on) => setActive(on ? i : null)}
            />
          ))}
        </ol>
      </div>

      <p className="art-fig-source">
        Source:{" "}
        {figure.source.href ? (
          <a href={figure.source.href}>{figure.source.text}</a>
        ) : (
          figure.source.text
        )}
      </p>
    </figure>
  );
}

function Row({
  row,
  kind,
  place,
  ticks,
  refs,
  active,
  onActive,
}: {
  row: FigureRow;
  kind: ArticleFigure["kind"];
  place: number;
  ticks: number[];
  refs: (Ref & { place: number })[];
  active: boolean;
  onActive: (on: boolean) => void;
}) {
  const name = row.detail ? `${row.label}, ${row.detail}` : row.label;
  return (
    <li
      className="art-chart-row"
      data-active={active ? "" : undefined}
      onPointerEnter={() => onActive(true)}
      onPointerLeave={() => onActive(false)}
    >
      <span className="art-chart-label">
        <span className="art-chart-name">{row.label}</span>
        {row.detail ? <span className="art-chart-detail">{row.detail}</span> : null}
      </span>
      <span className="art-chart-track">
        {ticks.map((tick) => (
          <span key={tick} className="art-chart-gridline" style={at(tick)} aria-hidden="true" />
        ))}
        {kind === "bars" ? (
          <span className="art-chart-bar" style={{ width: percent(place) }} aria-hidden="true" />
        ) : (
          <span className="art-chart-dot" style={at(place)} aria-hidden="true" />
        )}
        {refs.map((ref) => (
          <span
            key={ref.label}
            className="art-chart-ref"
            data-tone={ref.tone}
            style={at(ref.place)}
            aria-hidden="true"
          />
        ))}
        <span className="art-chart-value" style={after(place)}>
          {row.display}
        </span>
        {active ? (
          <span className="art-chart-tip" role="tooltip" style={after(place)}>
            <strong>{row.display}</strong> {name}
          </span>
        ) : null}
      </span>
    </li>
  );
}

/** Where a value sits on a log axis running from the first tick to the last, from 0 to 1. */
function logPosition(ticks: number[]) {
  const low = Math.log10(ticks[0]);
  const high = Math.log10(ticks[ticks.length - 1]);
  return (value: number) => (Math.log10(value) - low) / (high - low);
}

/** Positions run across the track, which stops short of its end to leave room for the last value. */
function percent(place: number): string {
  return `calc((100% - var(--art-chart-room)) * ${place.toFixed(4)})`;
}
function at(place: number): CSSProperties {
  return { left: percent(place) };
}
function after(place: number): CSSProperties {
  return { left: `calc((100% - var(--art-chart-room)) * ${place.toFixed(4)} + 10px)` };
}
